// src/views/knowledge-base/tutorialFixtures.ts
//
// EPHEMERAL TUTORIAL-ONLY FIXTURES
// ================================
// Some walkthroughs read better when a fake record (e.g. "Johnny Test") is
// visibly present in the list/record UI the tutorial touches — WITHOUT ever
// hitting the backend or persisting. This module declares those fixtures and
// the per-tutorial plan for when they appear.
//
// Hard invariants:
//  - Fixtures NEVER reach the backend. WalkthroughPlayer keeps blocking writes
//    (synthetic 200). These records are spliced into GET responses client-side
//    only, and removed the instant the walkthrough exits / tears down / errors
//    (the player restores window.fetch and full-reloads on exit).
//  - Every fixture id is prefixed `TUT-` and carries `_ephemeral: true` +
//    `_tutorial: true` so it can never be mistaken for, exported as, or saved
//    as real data.
//
// The fixture set is DECLARATIVE: add a builder to FIXTURES and reference its
// key from TUTORIAL_FIXTURES so any tutorial can request the same Johnny Test
// contact/lead.

export const EPHEMERAL_ID_PREFIX = 'TUT-'

export type FixtureKind = 'contact' | 'lead' | 'deal' | 'project'

export type EphemeralFixture = {
  key: string
  kind: FixtureKind
  id: string
  /** Build the raw backend-shaped record spliced into GET list/record JSON. */
  build: () => Record<string, unknown>
  /** True when a blocked create POST to this url should activate this fixture. */
  matchesCreate: (url: string) => boolean
}

const nowIso = () => new Date().toISOString()

// ── Johnny Test CONTACT (raw CrmClient shape consumed directly by the list) ──
function buildJohnnyContact(): Record<string, unknown> {
  return {
    unified_client_id: `${EPHEMERAL_ID_PREFIX}johnny-test-contact`,
    legacy_master_client_id: null,
    match_status: 'matched',
    client_type: 'person',
    preferred_name: 'Johnny Test',
    nickname: null,
    normalized_name: 'johnny test',
    preferred_street: '123 Main Street',
    preferred_city: 'Safety Harbor',
    preferred_state: 'FL',
    preferred_zip: '34695',
    preferred_phone: '(444) 444-4444',
    preferred_email: 'Johnny@test.com',
    contact_count: 1,
    lead_count: 0,
    deal_count: 0,
    latest_lead_id: null,
    latest_lead_title: null,
    latest_lead_status: null,
    latest_deal_id: null,
    latest_deal_title: null,
    latest_deal_stage: null,
    latest_pipeline_name: null,
    legacy_sources: null,
    legacy_total_invoiced: 0,
    legacy_total_paid: 0,
    legacy_balance: 0,
    legacy_total_invoices: 0,
    legacy_total_estimates: 0,
    legacy_total_docs: 0,
    legacy_job_count: 0,
    aliases: null,
    contact_type: 'person',
    contact_status: 'active',
    created_at: nowIso(),
    archived_at: null,
    merged_into: null,
    // Tutorial tags — never present on real records.
    _ephemeral: true,
    _tutorial: true,
  }
}

// ── Test Deal CONTACT (raw CrmClient shape) ──
// Used by the "Archive Contact(s)" walkthrough (tutorial 4): the user searches
// "test deal" in Contacts, selects this row, and clicks Archive. It is
// pre-injected so it already EXISTS when the search runs. Its name MUST contain
// "test deal" so the search-term splice (spliceListResponse, which honors the
// `?search=` param) surfaces it; the archive POST is a no-op because
// WalkthroughPlayer blocks every write to /api/crm/clients (synthetic 200).
function buildTestDealContact(): Record<string, unknown> {
  return {
    unified_client_id: `${EPHEMERAL_ID_PREFIX}test-deal-contact`,
    legacy_master_client_id: null,
    match_status: 'matched',
    client_type: 'person',
    preferred_name: 'Test Deal',
    nickname: null,
    normalized_name: 'test deal',
    preferred_street: '123 Main Street',
    preferred_city: 'Safety Harbor',
    preferred_state: 'FL',
    preferred_zip: '34695',
    preferred_phone: '(444) 444-4444',
    preferred_email: 'test.deal@example.com',
    contact_count: 1,
    lead_count: 0,
    deal_count: 0,
    latest_lead_id: null,
    latest_lead_title: null,
    latest_lead_status: null,
    latest_deal_id: null,
    latest_deal_title: null,
    latest_deal_stage: null,
    latest_pipeline_name: null,
    legacy_sources: null,
    legacy_total_invoiced: 0,
    legacy_total_paid: 0,
    legacy_balance: 0,
    legacy_total_invoices: 0,
    legacy_total_estimates: 0,
    legacy_total_docs: 0,
    legacy_job_count: 0,
    aliases: 'Test Deal',
    contact_type: 'person',
    contact_status: 'active',
    created_at: nowIso(),
    archived_at: null,
    merged_into: null,
    // Tutorial tags — never present on real records.
    _ephemeral: true,
    _tutorial: true,
  }
}

// ── Test Deal DEAL (raw shape consumed by mapDeal / the deals list) ──
// Comprehensive tutorial mock: alongside the Test Deal CONTACT, a Test Deal
// DEAL must also exist in tutorial mode so a tour that searches/clicks "Test
// Deal" on the #/deals "Open Deals" view (or in unified-search `deals[]`) finds
// it — e.g. a "Create a project from <contact>" tour, Step "Click Test deal".
// It is LINKED to the Test Deal contact (contact_id / unified_client_id /
// contact_name) so a deal -> contact / create-project-from-deal flow resolves.
// Spliced into GET /api/crm/deals?search=… AND unified-search deals[]; the deal
// record GET is also served so a row click can open it. All writes are no-op'd.
function buildTestDealDeal(): Record<string, unknown> {
  return {
    id: `${EPHEMERAL_ID_PREFIX}test-deal-deal`,
    title: 'Test Deal',
    stage_id: 'C1:NEW',
    stage_label: 'New',
    category_id: 0,
    pipeline_name: 'Sales',
    opportunity: 5000,
    assigned_by_id: null,
    // Link to the Test Deal CONTACT so deal -> contact navigation resolves.
    contact_id: `${EPHEMERAL_ID_PREFIX}test-deal-contact`,
    contact_name: 'Test Deal',
    unified_client_id: `${EPHEMERAL_ID_PREFIX}test-deal-contact`,
    project_count: 0,
    ticket_count: 0,
    company_id: null,
    lead_id: null,
    comments: null,
    source_id: null,
    sales_person: null,
    community_name: null,
    referral_name: null,
    close_date: '',
    date_create: nowIso(),
    date_modify: nowIso(),
    estimate_appointment: null,
    notes: null,
    loss_reason: null,
    reschedule_count: 0,
    deal_type: 'residential',
    is_hoa: 0,
    // Tutorial tags — never present on real records.
    _ephemeral: true,
    _tutorial: true,
  }
}

// ── Test Project PROJECT (raw CrmProject shape / the #/projects list) ──
// Comprehensive tutorial mock: alongside the Test Deal contact + deal, a Test
// Project must exist in tutorial mode so a tour that types/searches "Test
// Project" on the #/projects "All projects" view finds it — e.g. a "Create a
// project from <contact>" tour, Step "Type Test Project". LINKED to the Test
// Deal contact (client_id / contact_id / client_name) and the Test Deal deal
// (deal_id) so project -> contact / deal flows resolve. The projects list
// returns its rows under the `data` key (NOT `projects`); spliced into GET
// /api/crm/projects?search=… honoring the typed search, and the project record
// GET is served so a row click can open it. All writes stay no-op'd.
function buildTestProject(): Record<string, unknown> {
  return {
    id: `${EPHEMERAL_ID_PREFIX}test-project`,
    name: 'Test Project',
    // Link to the Test Deal CONTACT (CONTACT column) + the Test Deal deal.
    client_id: `${EPHEMERAL_ID_PREFIX}test-deal-contact`,
    client_name: 'Test Deal',
    contact_id: `${EPHEMERAL_ID_PREFIX}test-deal-contact`,
    deal_id: `${EPHEMERAL_ID_PREFIX}test-deal-deal`,
    pipeline_stage: 'P_IN_PROGRESS',
    estimator_id: null,
    estimator_name: null,
    services_requested: null,
    po_number: null,
    contract_value: 5000,
    status: 'in_progress',
    progress: 25,
    start_date: null,
    end_date: null,
    value: 5000,
    assigned_to: null,
    description: null,
    address: null,
    created_at: nowIso(),
    updated_at: nowIso(),
    source: 'crm_pipeline',
    owner_id: null,
    owner_name: null,
    owner_approval_status: null,
    owner_discount_percent: 0,
    // Tutorial tags — never present on real records.
    _ephemeral: true,
    _tutorial: true,
  }
}

// ── Test Project SUB-ADMIN DETAIL (owner-approval tour, tutorial 11) ──
// The injected demo owner's id (Johnny Test), seeded client-side into the
// sub-admin users list (src/api.ts buildDemoUser, role:'owner') during a
// walkthrough. The Approve gate in Subcontractors.tsx requires
// subPreviewUserId === project.owner_id, so the fixture's owner_id MUST equal
// this so previewing AS Johnny Test unlocks the Approve button.
export const TUT_SUB_DEMO_OWNER_ID = 'demo-user-delete-me'

// Sub-admin PROJECT-DETAIL fixture for "Approving a Project for Owner" (tut 11).
// The CRM "Open in Subcontractor Portal" button navigates the embedded portal to
// /subcontractors/projects/TUT-test-project, whose detail GET
// (/api/sub-admin/projects/TUT-test-project) 404s in the sandbox — there is no
// such row in SUB_DB — leaving subProjectDetail null and the view stuck on a
// spinner. WalkthroughPlayer serves THIS fixture instead so the portal
// project-detail renders as an OWNER-approvable project: owner_approval_status
// 'pending' + owner_id = the injected demo owner (Johnny Test). Combined with
// previewing AS that owner (subPreviewUserId === owner_id), the Subcontractors
// detail view renders the Approve button (data-tour-id="sub-project-approve")
// that tutorial-11 step 16 spotlights. The owner-approve PATCH is a no-op
// (blocked by the player's synthetic-200 write shim), so nothing persists.
// Returned UNDER `data` to match fetchSubAdminProject's { success, data } shape
// where the view reads data.project.*.
export function buildTestSubProjectDetail(): Record<string, unknown> {
  return {
    project: {
      id: `${EPHEMERAL_ID_PREFIX}test-project`,
      name: 'Test Project',
      task_count: 4,
      completed_task_count: 1,
      owner_approval_status: 'pending',
      owner_id: TUT_SUB_DEMO_OWNER_ID,
      owner_display: 'Johnny Test',
      owner_name: 'Johnny Test',
      owner_discount_percent: 0,
      status: 'in_progress',
      started_at: null,
      paused_at: null,
      completion_requested_at: null,
      latest_chat_user_role: null,
      owner_unread_count: 0,
      admin_unread_count: 0,
      latest_denial_reason: null,
      site_address: '123 Main Street, Safety Harbor, FL 34695',
      // Tutorial tags — never present on real records.
      _ephemeral: true,
      _tutorial: true,
    },
    tasks: [],
    // Tutorial tags on the wrapper too, for defense in depth.
    _ephemeral: true,
    _tutorial: true,
  }
}

// True for the sub-admin project-DETAIL GET of the Test Project fixture:
//   GET /api/sub-admin/projects/TUT-test-project
// (NOT its sub-resources like …/tasks, …/files — those degrade to empty on the
// caller side via .catch, so only the detail endpoint needs serving.)
export function isTutSubProjectDetailUrl(pathname: string): boolean {
  const m = pathname.match(/\/api\/sub-admin\/projects\/([^/]+)\/?$/i)
  if (!m) return false
  return decodeURIComponent(m[1]) === `${EPHEMERAL_ID_PREFIX}test-project`
}

// ── Johnny Test LEAD (raw shape consumed by mapLead) ──
function buildJohnnyLead(): Record<string, unknown> {
  return {
    id: `${EPHEMERAL_ID_PREFIX}johnny-test-lead`,
    title: 'Johnny Test',
    customer_name: 'Johnny Test',
    status_id: 'NEW',
    status_label: 'New',
    source_id: '',
    source_label: '',
    opportunity: 0,
    assigned_by_id: null,
    phone: '(444) 444-4444',
    email: 'Johnny@test.com',
    comments: null,
    address: '123 Main Street',
    address_city: 'Safety Harbor',
    address_state: 'FL',
    address_zip: '34695',
    sales_person: null,
    community_name: null,
    referral_name: null,
    property_type: null,
    unified_client_id: `${EPHEMERAL_ID_PREFIX}johnny-test-contact`,
    date_create: nowIso(),
    date_modify: nowIso(),
    last_called_at: null,
    last_emailed_at: null,
    overdue: null,
    suggested_users: [],
    suggested_user_ids: [],
    // Tutorial tags.
    _ephemeral: true,
    _tutorial: true,
  }
}

// Collection-create matcher: POST whose path ends in the collection name.
const createMatcher = (collection: string) => (url: string): boolean => {
  try {
    const path = new URL(url, 'http://x').pathname.replace(/\/$/, '')
    return new RegExp(`/api/crm/${collection}$`, 'i').test(path)
  } catch {
    return false
  }
}

export const FIXTURES: Record<string, EphemeralFixture> = {
  'johnny-contact': {
    key: 'johnny-contact',
    kind: 'contact',
    id: `${EPHEMERAL_ID_PREFIX}johnny-test-contact`,
    build: buildJohnnyContact,
    matchesCreate: createMatcher('clients'),
  },
  'johnny-lead': {
    key: 'johnny-lead',
    kind: 'lead',
    id: `${EPHEMERAL_ID_PREFIX}johnny-test-lead`,
    build: buildJohnnyLead,
    matchesCreate: createMatcher('leads'),
  },
  'test-deal-contact': {
    key: 'test-deal-contact',
    kind: 'contact',
    id: `${EPHEMERAL_ID_PREFIX}test-deal-contact`,
    build: buildTestDealContact,
    matchesCreate: createMatcher('clients'),
  },
  'test-deal-deal': {
    key: 'test-deal-deal',
    kind: 'deal',
    id: `${EPHEMERAL_ID_PREFIX}test-deal-deal`,
    build: buildTestDealDeal,
    matchesCreate: createMatcher('deals'),
  },
  'test-project': {
    key: 'test-project',
    kind: 'project',
    id: `${EPHEMERAL_ID_PREFIX}test-project`,
    build: buildTestProject,
    matchesCreate: createMatcher('projects'),
  },
}

export type TutorialFixturePlan = {
  /** Fixtures present from the moment the tutorial starts. */
  preInject?: string[]
  /** Fixtures that appear only AFTER their matching blocked create POST. */
  onCreate?: string[]
}

const normalizeTitle = (t: string | undefined | null) =>
  (t || '').trim().toLowerCase().replace(/\s+/g, ' ')

// Fixtures pre-injected for EVERY tutorial (any title). The fake "Test Deal"
// contact is globally present in tutorial mode so ANY walkthrough that searches
// "test deal" (e.g. the archive-contact tour AND any other tour referencing it)
// finds it — not just one opted-in title. It's harmless: it only exists while a
// walkthrough is active (spliced into GET responses client-side), is tagged
// TUT-/_ephemeral/_tutorial so it can never persist or export, all writes to it
// are no-op'd by WalkthroughPlayer, and it's cleared on teardown + reload.
const GLOBAL_PREINJECT: string[] = ['test-deal-contact', 'test-deal-deal', 'test-project']

// Keyed by normalized tutorial title. Add entries here to opt a SPECIFIC
// tutorial into additional fixtures beyond the global default above.
const TUTORIAL_FIXTURES_BY_TITLE: Record<string, TutorialFixturePlan> = {
  // The create-contact walkthrough: the Johnny Test contact materializes in the
  // Contacts list right after the (blocked) Save, so the flow feels real.
  'add a new contact': { onCreate: ['johnny-contact'] },
}

export function fixturePlanFor(opts: { title?: string | null; id?: number | null }): TutorialFixturePlan | null {
  // No title ⇒ no tutorial is active ⇒ no fixtures (mock absent in normal use).
  const norm = normalizeTitle(opts.title)
  if (!norm) return null
  const byTitle = TUTORIAL_FIXTURES_BY_TITLE[norm]
  // Merge the global default pre-injects with any title-specific plan, de-duped,
  // so the Test Deal mock is present for EVERY tutorial while title-specific
  // fixtures (e.g. Johnny Test on create) still apply.
  const preInject = Array.from(new Set([...GLOBAL_PREINJECT, ...(byTitle?.preInject || [])]))
  const plan: TutorialFixturePlan = { preInject }
  if (byTitle?.onCreate) plan.onCreate = byTitle.onCreate
  return plan
}

// Minimal view of the unified-search response (GET /api/crm/search/unified?q=…)
// that powers the Create-New / Smart-Create contact-suggestion dropdown.
export type UnifiedSearchLike = {
  contacts?: unknown[]
  leads?: unknown[]
  deals?: unknown[]
  projects?: unknown[]
  total?: number
}

/**
 * Splice active ephemeral fixtures into a UNIFIED-SEARCH response so the
 * tutorial-only mock (e.g. "Test Deal") also appears in the Create-New contact
 * autocomplete dropdown — not just the Contacts LIST. Contact fixtures go into
 * `contacts` (id `unified_client_id`), lead fixtures into `leads` (id `id`),
 * deal fixtures into `deals` (id `id`).
 *
 * Tutorial-mode-only by construction: the caller passes only the fixtures that
 * are ACTIVE during a running walkthrough (empty ⇒ nothing injected ⇒ the mock
 * is absent in normal usage). Honors the typed query so a fixture surfaces only
 * when the search term would match it (e.g. typing "Test Deal"). Mutates `data`
 * in place and returns the number of records added (0 ⇒ unchanged). Writes are
 * unaffected — WalkthroughPlayer still no-ops every backend mutation.
 */
export function spliceFixturesIntoUnified(
  data: UnifiedSearchLike,
  fixtures: EphemeralFixture[],
  q: string,
): number {
  const term = (q || '').trim().toLowerCase()
  let added = 0
  for (const fx of fixtures) {
    const rec = fx.build()
    // Honor an active query so the fixture doesn't surface under an unrelated
    // search — it should only show when the search would match it.
    if (term && !JSON.stringify(rec).toLowerCase().includes(term)) continue
    if (fx.kind === 'contact') {
      if (!Array.isArray(data.contacts)) continue
      const id = (rec as Record<string, unknown>).unified_client_id
      if (data.contacts.some((r) => String((r as Record<string, unknown>)?.unified_client_id) === String(id))) continue
      data.contacts.unshift(rec)
      added++
    } else if (fx.kind === 'lead') {
      if (!Array.isArray(data.leads)) continue
      const id = (rec as Record<string, unknown>).id
      if (data.leads.some((r) => String((r as Record<string, unknown>)?.id) === String(id))) continue
      data.leads.unshift(rec)
      added++
    } else if (fx.kind === 'deal') {
      if (!Array.isArray(data.deals)) continue
      const id = (rec as Record<string, unknown>).id
      if (data.deals.some((r) => String((r as Record<string, unknown>)?.id) === String(id))) continue
      data.deals.unshift(rec)
      added++
    } else if (fx.kind === 'project') {
      // Unified-search has no projects[] today; this branch is a no-op unless a
      // future response carries one. The projects LIST GET (handled by the
      // WalkthroughPlayer shim) is the surface that matters for the mock.
      if (!Array.isArray(data.projects)) continue
      const id = (rec as Record<string, unknown>).id
      if (data.projects.some((r) => String((r as Record<string, unknown>)?.id) === String(id))) continue
      data.projects.unshift(rec)
      added++
    }
  }
  if (added && typeof data.total === 'number') data.total += added
  return added
}
